import { Psychology } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import * as R from "ramda";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FeatureFlag } from "../../../common/posthog";
import { hasScoreWarning, ScoreBar } from "../../../components/ScoreBar";
import {
  TrendIcon
} from "../../../components/TrendIcon";
import { AggregatedAttributePerformanceData } from "../../../data/queries/performanceAnalysis";
import { formatTrendPercentage } from "../../../utils/number";
import { CONVERSATION_LENS } from "../../../utils/terminology";
import CoachingConfirmDialog from "./CoachingConfirmDialog";
import NoBestPerformerWarning from "./NoBestPerformerWarning";
import VsNumbers from "./VsNumbers";

interface AttributeAnalysisTableProps {
  data: AggregatedAttributePerformanceData[];
  isLoading: boolean;
  error: Error | null;
  userFilter?: string;
  lensName: string;
  title: string;
}

const AttributeAnalysisTable = ({
  title,
  data,
  isLoading,
  error,
  userFilter,
  lensName
}: AttributeAnalysisTableProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isUserSelected = userFilter && userFilter !== "all";
  const isLensSelected = lensName && lensName !== "all";
  const isCoachingEnabled = isUserSelected && isLensSelected;

  const timeRange = searchParams.get("timeRange") || "last7Days";

  const handleCoachingClick = () => {
    setIsDialogOpen(true);
  };

  const handleConfirmCoaching = () => {
    setIsDialogOpen(false);
    const currentParams = Object.fromEntries(searchParams.entries());
    currentParams.startGenerate = "true";
    const queryString = new URLSearchParams(currentParams).toString();
    navigate(`/?${queryString}`);
  };

  const handleCancelCoaching = () => {
    setIsDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    const isTimeout = error.message?.toLowerCase().includes("timeout") ||
                      error.message?.toLowerCase().includes("canceling statement");

    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Alert severity={isTimeout ? "info" : "error"}>
          {isTimeout ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Query took too long to complete
              </Typography>
              <Typography variant="body2">
                Please reduce the time range or the number of teams/people in the filters to get the attribute performance details.
              </Typography>
            </>
          ) : (
            `Failed to load attribute performance: ${error.message}`
          )}
        </Alert>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" align="center" sx={{ py: 2 }}>
          No attribute performance data available
        </Typography>
      </Paper>
    );
  }

  const sortedData = R.sortWith([
    R.descend(R.prop("scored_count")),
    R.ascend(R.prop("attr_name"))
  ], data);

  return (
    <Paper sx={{ mt: 3 }}>
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h6">{title} - {lensName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Performance metrics for each attribute within the selected {CONVERSATION_LENS}
          </Typography>
        </Box>
        <FeatureFlag flag="coaching" fallback={<></>}>
          <Tooltip
            title={isCoachingEnabled ? "" : "Please select a user and a lens to run coaching"}
            placement="left"
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Psychology />}
                onClick={handleCoachingClick}
                disabled={!isCoachingEnabled}
                sx={{ flexShrink: 0 }}
              >
                Coaching
              </Button>
            </span>
          </Tooltip>
        </FeatureFlag>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 320, color: "text.secondary" }}>Attribute</TableCell>
              <TableCell sx={{ width: 300, minWidth: 250, color: "text.secondary" }}>Performance</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Team</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Best</TableCell>
              <TableCell sx={{ width: 100, minWidth: 100, color: "text.secondary" }}>Trend</TableCell>
              <TableCell align="right" sx={{ width: 100, color: "text.secondary" }}># Scored</TableCell>
              <TableCell align="right" sx={{ width: 110, color: "text.secondary" }}># Not Scored</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((attribute) => {
              const showWarning = hasScoreWarning(attribute.scored_count, attribute.stddev_score);
              return (
                <TableRow
                  key={attribute.attr_name}
                  hover
                  sx={{
                    backgroundColor: showWarning ? "#f8f0ea" : "inherit"
                  }}
                >
                  <TableCell>
                    {attribute.attr_name}
                  </TableCell>

                  <TableCell>
                    <ScoreBar
                      showRange={true}
                      scoreRange={[0, 5]}
                      minScore={attribute.min_score}
                      maxScore={attribute.max_score}
                      score={attribute.avg_score || 0}
                      overlayScores={[
                        { score: attribute.avg_score, label: "Average Score" }
                      ]}
                      overlayTopScores={attribute.best_user_email ? [
                        { score: attribute.best_user_avg_score, label: `Best User: ${attribute.best_user_email}` }
                      ] : []}
                      count={attribute.scored_count}
                      stddev={attribute.stddev_score}
                      showConfidenceInterval={true}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <VsNumbers
                      percentageDelta={attribute.pct_delta_vs_group_avg}
                      absoluteDelta={attribute.delta_vs_group_avg}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {attribute.best_user_email ? (
                      <VsNumbers
                        percentageDelta={attribute.pct_delta_vs_best}
                        absoluteDelta={attribute.delta_vs_best}
                      />
                    ) : (
                      <NoBestPerformerWarning message="Not enough data to determine best performer for this attribute" />
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {attribute.pct_delta_vs_prev_avg && <TrendIcon trendPercentage={attribute.pct_delta_vs_prev_avg} />}
                      <Typography
                        variant="body2"
                        sx={{
                          color: attribute.pct_delta_vs_prev_avg > 0 ? "success.main" :
                            attribute.pct_delta_vs_prev_avg < 0 ? "error.main" : "text.secondary"
                        }}
                      >
                        {formatTrendPercentage(attribute.pct_delta_vs_prev_avg)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    {attribute.scored_count.toLocaleString()}
                  </TableCell>

                  <TableCell align="right">
                    {attribute.not_scored_count.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CoachingConfirmDialog
        open={isDialogOpen}
        onConfirm={handleConfirmCoaching}
        onCancel={handleCancelCoaching}
        agentEmail={userFilter || ""}
        lensName={lensName}
        timeRange={timeRange}
      />
    </Paper>
  );
};

export default AttributeAnalysisTable;
